import {
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import MessageIcon from '@assets/MessageIcon.svg'
import { useAppTheme } from '@/theme/ThemeContext'
import PageStateMessage from '@/components/common/PageStateMessage'
import { describeGatewayError } from '@/utils/gatewayError'
import type { SentMessagesList } from '@/hooks/useSentMessages'
import MessageCard from './MessageCard'

export type MessagesTab = 'general' | 'alert'

type Props = {
  tab: MessagesTab
  onTabChange: (tab: MessagesTab) => void
  /** התראה ספציפית stays disabled until an alert row is picked. */
  alertSelected: boolean
  list: SentMessagesList
  onAdd?: () => void
  enableAdd?: boolean
}

const SentMessagesPanel = ({
  tab,
  onTabChange,
  alertSelected,
  list,
  onAdd,
  enableAdd = false,
}: Props) => {
  const { mode } = useAppTheme()
  const dark = mode === 'dark'
  const colors = {
    text: dark ? '#fff' : '#003F61',
    muted: dark ? '#6B7A8C' : '#8F98A5',
    border: dark ? '#445876' : '#E3E8EF',
    divider: dark ? '#445876' : '#EDF2F7',
    card: dark ? '#0D1A33' : '#fff',
    scrollTrack: dark ? '#202B40' : '#F1F5F9',
    scrollThumb: dark ? '#445876' : '#CBD5E1',
  }
  const font = { fontFamily: 'Rubik, sans-serif' }
  const loading = list.status === 'loading' || list.status === 'idle'

  return (
    <Paper
      elevation={0}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        direction: 'rtl',
        px: 2,
        pt: 0.5,
        bgcolor: colors.card,
        border: `1px solid ${colors.border}`,
        borderRadius: '8px',
        boxShadow: 'none',
        minHeight: 0,
      }}
    >
      <Tabs
        value={tab}
        onChange={(_, next: MessagesTab) => onTabChange(next)}
        variant="fullWidth"
        sx={{
          minHeight: 36,
          '& .MuiTab-root': {
            minHeight: 36,
            py: 0.5,
            fontSize: '14px',
            ...font,
            color: colors.muted,
          },
          '& .Mui-selected': { color: `${colors.text} !important` },
        }}
      >
        <Tab value="general" label="כללי" />
        <Tab value="alert" label="התראה ספציפית" disabled={!alertSelected} />
      </Tabs>
      <Divider sx={{ borderColor: colors.divider }} />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 1 }}>
        <Box
          component="img"
          src={MessageIcon}
          alt=""
          sx={{ filter: dark ? 'brightness(0) invert(1)' : 'none' }}
        />
        <Typography
          sx={{
            fontWeight: 800,
            color: colors.text,
            fontSize: '18px',
            ...font,
          }}
        >
          הודעות
        </Typography>
        <Typography
          sx={{
            fontWeight: 600,
            color: colors.muted,
            fontSize: '18px',
            ...font,
          }}
        >
          {list.status === 'ready' ? list.total : ''}
        </Typography>
        <IconButton
          size="small"
          aria-label="add"
          onClick={enableAdd ? onAdd : undefined}
          sx={{
            marginInlineStart: 'auto',
            width: 43,
            height: 43,
            borderRadius: 1.5,
            border: `1px solid ${colors.border}`,
            bgcolor: colors.card,
            cursor: enableAdd ? 'pointer' : 'not-allowed',
            '&:hover': { bgcolor: colors.card },
          }}
        >
          <AddIcon sx={{ color: colors.text }} />
        </IconButton>
      </Box>
      <Divider sx={{ borderColor: colors.divider }} />

      <Stack
        spacing={5}
        sx={{
          px: 1,
          py: 1.5,
          overflowY: 'auto',
          flex: 1,
          minHeight: 0,
          '&::-webkit-scrollbar': { width: '6px' },
          '&::-webkit-scrollbar-track': {
            backgroundColor: colors.scrollTrack,
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: colors.scrollThumb,
            borderRadius: '4px',
          },
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            {list.status === 'ready' && list.items.length === 0 && (
              <Typography
                variant="body2"
                sx={{
                  color: colors.muted,
                  textAlign: 'center',
                  mt: 2,
                  ...font,
                }}
              >
                לא נמצאו הודעות ללקוח זה.
              </Typography>
            )}
            {list.items.map(message => (
              <MessageCard
                key={message.messageId}
                message={message}
                showAlertType={tab === 'general'}
              />
            ))}
            {list.status === 'loadingMore' && (
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={22} />
              </Box>
            )}
            {list.hasMore && (
              <Button onClick={list.loadMore} sx={{ ...font }}>
                טען עוד
              </Button>
            )}
            {list.status === 'error' && (
              <PageStateMessage
                tone="error"
                title="לא ניתן לטעון הודעות"
                detail={describeGatewayError(list.error, 'טעינת ההודעות נכשלה')}
                onRetry={list.retry}
              />
            )}
          </>
        )}
      </Stack>
    </Paper>
  )
}

export default SentMessagesPanel
